import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useT } from "@/i18n/LanguageContext";
import api from "@/lib/api";
import { asArray, asStringArray, formatCr } from "@/lib/safeData";
import { ArrowLeft, AlertTriangle, Users, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";

const statusClass = (s) => ({
  high_risk: "tag-high-risk",
  reported_cybercrime: "tag-cybercrime",
  sebi_warning: "tag-sebi-warning",
  multiple_complaints: "tag-multiple",
  under_investigation: "tag-investigating",
}[s] || "tag-investigating");

const ScammerDetail = () => {
  const { t } = useT();
  const { id } = useParams();
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/scammers/${id}`).then((r) => setS(r.data)).catch(() => setS(null)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="py-32 text-center text-white/50">{t.common.loading}</div>;
  if (!s) return (
    <div className="py-32 text-center">
      <div className="text-white/60">Not found</div>
      <Link to="/database"><Button variant="outline" className="mt-4 border-white/20">{t.common.backHome}</Button></Link>
    </div>
  );

  return (
    <div className="mx-auto max-w-5xl px-4 md:px-8 py-16" data-testid="scammer-detail">
      <Link to="/database" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white" data-testid="back-to-db">
        <ArrowLeft className="h-4 w-4" /> {t.nav.database}
      </Link>

      <div className="mt-6 surface-card p-6 md:p-10">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-widest font-mono-alt ${statusClass(s.status_tag)}`}>
              <AlertTriangle className="h-3 w-3" />
              {t.database.statusLabels[s.status_tag] || s.status_tag}
            </span>
            <h1 className="mt-3 font-display text-3xl md:text-4xl font-black tracking-tight">{s.handle}</h1>
            <div className="mt-1 text-sm text-white/50 font-mono-alt">{asStringArray(s.aliases).join(" · ")}</div>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-widest text-white/40 font-mono-alt">{t.database.lossReported}</div>
            <div className="mt-1 font-mono-alt text-2xl font-bold text-[#FF6A60]">{formatCr(s.total_loss_inr)}</div>
            <div className="mt-2 inline-flex items-center gap-1.5 text-white/65 text-sm"><Users className="h-4 w-4" /> {s.victim_count} {t.database.victims}</div>
          </div>
        </div>

        <p className="mt-6 text-white/75 leading-relaxed">{s.summary}</p>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            ["Telegram", s.telegram_username],
            ["UPI", s.upi_id],
            ["Phone", s.phone],
            ["Company", s.company_name],
            ["Instagram", s.instagram_handle],
            ["Website", s.website],
          ].map(([k, v]) => v && (
            <div key={k} className="rounded-lg border border-white/10 bg-white/[0.02] px-4 py-3">
              <div className="text-[10px] uppercase tracking-widest text-white/40 font-mono-alt">{k}</div>
              <div className="mt-1 text-sm font-mono-alt text-white/90 break-all">{v}</div>
            </div>
          ))}
        </div>

        {asStringArray(s.scam_methods).length > 0 && (
          <div className="mt-8">
            <div className="text-[11px] uppercase tracking-widest text-[#D4AF37] font-mono-alt">Methods</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {asStringArray(s.scam_methods).map((m) => (
                <span key={m} className="text-xs text-white/80 bg-white/5 border border-white/10 px-3 py-1.5 rounded-full">{m}</span>
              ))}
            </div>
          </div>
        )}

        {asArray(s.timeline).length > 0 && (
          <div className="mt-10">
            <div className="text-[11px] uppercase tracking-widest text-[#D4AF37] font-mono-alt">Timeline</div>
            <div className="mt-4 space-y-3">
              {asArray(s.timeline).map((e, i) => (
                <div key={i} className="flex items-start gap-3 rounded-lg border border-white/10 bg-white/[0.02] px-4 py-3">
                  <Calendar className="h-4 w-4 text-[#D4AF37] mt-0.5" />
                  <div>
                    <div className="text-xs text-white/50 font-mono-alt">{e.date}</div>
                    <div className="text-sm text-white/85">{e.event}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/report"><Button className="bg-[#FF3B30] hover:bg-[#cc2e25] font-bold" data-testid="detail-report-btn">{t.nav.reportCta}</Button></Link>
          <Link to="/risk-checker"><Button variant="outline" className="border-white/20">{t.nav.risk}</Button></Link>
        </div>
      </div>
    </div>
  );
};

export default ScammerDetail;
